/**
 * AuthService - Singleton service for Clerk authentication token management
 */

import { useAuth } from '@clerk/clerk-react';

// Define a type for the Window object with Clerk
interface WindowWithClerk extends Window {
  Clerk?: {
    session?: {
      getToken(): Promise<string | null>
    }
  }
}

// TODO: Add custom auth error classes (AuthError, TokenExpiredError, NoTokenError)
// TODO: Implement more specific error handling with proper error types

export class AuthService {
  private static instance: AuthService;

  private constructor() {
    // Private constructor to prevent direct instantiation
  }

  /**
   * Get the singleton instance of AuthService
   */
  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * Get the current authentication token from Clerk
   * @returns The authentication token or null if not available
   */
  public async getToken(): Promise<string | null> {
    try {
      // Use window.Clerk object to get the token
      if (!(window as WindowWithClerk).Clerk?.session) {
        console.warn('Clerk session not available');
        // TODO: Throw NoTokenError instead of returning null
        return null;
      }
      
      return await (window as WindowWithClerk).Clerk?.session?.getToken() || null;
    } catch (error) {
      console.error('Failed to get auth token:', error);
      // TODO: Transform into specific auth error types based on the error
      return null;
    }
  }

  /**
   * Check if the user is authenticated
   * @returns True if authenticated, false otherwise
   */
  public async isAuthenticated(): Promise<boolean> {
    const token = await this.getToken();
    return !!token;
  }
  
  // TODO: Add token refresh and expiration detection methods
  // TODO: Add signOut method that clears auth state
}

// Export a pre-initialized instance
export const authService = AuthService.getInstance(); 